import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import { useNavigate } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import TopBar from "../components/TopBar";
import EditPlayerModal from "../components/EditPlayerModal";
import SharePanel from "../components/SharePanel";
import PrivateRequestsPanel from "../components/PrivateRequestsPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(() => !!localStorage.getItem("st_admin_pw"));
  const [pw, setPw] = useState("");
  const [error, setError] = useState("");
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterPos, setFilterPos] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);

  const password = localStorage.getItem("st_admin_pw") || "";

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/players`, {
        headers: { "X-Admin-Password": password },
      });
      setPlayers(r.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("st_admin_pw");
        setAuthed(false);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) load();
    // eslint-disable-next-line
  }, [authed]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await axios.post(`${API}/admin/verify`, { password: pw });
      localStorage.setItem("st_admin_pw", pw);
      setAuthed(true);
      setPw("");
    } catch {
      setError(t("invalid_password"));
    }
  };

  const logout = () => {
    localStorage.removeItem("st_admin_pw");
    setAuthed(false);
    setPlayers([]);
  };

  const handleSaved = (updated) => {
    setPlayers((arr) => arr.map((p) => (p.id === updated.id ? updated : p)));
    setEditing(null);
  };

  const handleDelete = async (p) => {
    setDeleteError("");
    setDeleteBusy(true);
    try {
      await axios.delete(`${API}/admin/players/${p.id}`, {
        headers: { "X-Admin-Password": password },
      });
      setPlayers((arr) => arr.filter((x) => x.id !== p.id));
      setDeleting(null);
    } catch (e) {
      setDeleteError(e?.response?.data?.detail || "Error");
    } finally {
      setDeleteBusy(false);
    }
  };

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return players.filter((p) => {
      if (filterPos && p.position !== filterPos) return false;
      if (!s) return true;
      const blob = `${p.player_number} ${p.first_name} ${p.last_name} ${p.position} ${p.parent_full_name} ${p.email} ${p.current_club}`.toLowerCase();
      return blob.includes(s);
    });
  }, [players, search, filterPos]);

  const exportCSV = () => {
    const headers = [
      "Number",
      "First Name",
      "Last Name",
      "Age",
      "DOB",
      "Gender",
      "Position",
      "Foot",
      "Experience",
      "Club",
      "Parent Name",
      "Relationship",
      "Email",
      "Phone",
      "Emergency Name",
      "Emergency Phone",
      "Medical Condition",
      "Medical Details",
      "Allergies",
      "Medications",
      "Guardian Printed",
      "Signed Date",
      "Language",
      "Registered",
    ];
    const rows = filtered.map((p) => [
      p.player_number,
      p.first_name,
      p.last_name,
      p.age,
      p.date_of_birth,
      p.gender,
      p.position,
      p.dominant_foot,
      p.experience,
      p.current_club,
      p.parent_full_name,
      p.relationship,
      p.email,
      p.phone,
      p.emergency_contact?.name,
      p.emergency_contact?.phone,
      p.has_medical_condition ? "Yes" : "No",
      p.medical_condition_details,
      p.allergies,
      p.medications,
      p.guardian_printed_name,
      p.signed_date,
      p.language,
      p.timestamp,
    ]);
    const csv = [headers, ...rows]
      .map((r) =>
        r
          .map((c) => {
            const v = (c ?? "").toString().replace(/"/g, '""');
            return /[",\n]/.test(v) ? `"${v}"` : v;
          })
          .join(",")
      )
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `soccer-troop-players-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = (p) => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const W = doc.internal.pageSize.getWidth();
    // Header band
    doc.setFillColor(232, 85, 154);
    doc.rect(0, 0, W, 60, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.text("SOCCER TROOP", 40, 38);
    doc.setFontSize(10);
    doc.text("Player Registration Form", W - 40, 38, { align: "right" });

    let y = 90;
    const line = (label, value) => {
      doc.setTextColor(120, 120, 120);
      doc.setFontSize(9);
      doc.text(label.toUpperCase(), 40, y);
      doc.setTextColor(20, 20, 20);
      doc.setFontSize(11);
      doc.text(String(value ?? "—"), 200, y);
      y += 18;
    };

    // Big number
    doc.setFontSize(48);
    doc.setTextColor(232, 85, 154);
    doc.text(`#${p.player_number}`, W - 40, 110, { align: "right" });

    doc.setTextColor(20, 20, 20);
    doc.setFontSize(16);
    doc.text(`${p.first_name} ${p.last_name}`, 40, 110);

    y = 140;
    line("Player Number", p.player_number);
    line("Date of Birth", p.date_of_birth);
    line("Age", p.age);
    line("Gender", p.gender);
    line("Position", p.position);
    line("Dominant Foot", p.dominant_foot);
    line("Experience", p.experience);
    line("Current Club", p.current_club || "—");

    y += 10;
    doc.setFontSize(13);
    doc.setTextColor(232, 85, 154);
    doc.text("PARENT / GUARDIAN", 40, y);
    y += 16;
    doc.setTextColor(20, 20, 20);
    line("Full Name", p.parent_full_name);
    line("Relationship", p.relationship);
    line("Email", p.email);
    line("Phone / WhatsApp", p.phone);
    line("Emergency Contact", `${p.emergency_contact?.name} · ${p.emergency_contact?.phone}`);

    y += 10;
    doc.setFontSize(13);
    doc.setTextColor(232, 85, 154);
    doc.text("MEDICAL & INSURANCE", 40, y);
    y += 16;
    doc.setTextColor(20, 20, 20);
    line("Medical Condition", p.has_medical_condition ? "Yes" : "No");
    if (p.has_medical_condition) line("Details", p.medical_condition_details);
    line("Allergies", p.allergies || "—");
    line("Medications", p.medications || "—");

    y += 10;
    doc.setFontSize(13);
    doc.setTextColor(232, 85, 154);
    doc.text("CONSENT & SIGNATURE", 40, y);
    y += 16;
    doc.setTextColor(20, 20, 20);
    const c = p.consent || {};
    line("Marketing", c.marketing ? "Accepted" : "—");
    line("Social Media", c.social_media ? "Accepted" : "—");
    line("Insurance Waiver", c.insurance_waiver ? "Accepted" : "—");
    line("Liability Waiver", c.liability_waiver ? "Accepted" : "—");
    line("General", c.general ? "Accepted" : "—");
    line("Guardian Printed Name", p.guardian_printed_name);
    line("Date Signed", p.signed_date);

    if (p.signature_base64) {
      try {
        if (y > 600) {
          doc.addPage();
          y = 60;
        }
        doc.setFontSize(9);
        doc.setTextColor(120, 120, 120);
        doc.text("SIGNATURE", 40, y + 14);
        doc.addImage(p.signature_base64, "PNG", 40, y + 20, 240, 90);
        y += 120;
      } catch {
        /* ignore */
      }
    }

    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(
      `Registered: ${p.timestamp}  ·  Language: ${p.language?.toUpperCase()}`,
      40,
      doc.internal.pageSize.getHeight() - 30
    );

    doc.save(`soccer-troop-${p.player_number}-${p.last_name || "player"}.pdf`);
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#080808] text-white">
        <TopBar />
        <div className="max-w-md mx-auto px-4 pt-20">
          <div className="rounded-2xl bg-[#161616] border border-[#252525] p-8">
            <div className="flex items-center gap-3 mb-1">
              <span className="text-2xl">🔒</span>
              <h1 className="font-bebas text-3xl tracking-widest">{t("admin_title")}</h1>
            </div>
            <p className="text-sm text-neutral-400 mb-6">{t("admin_subtitle")}</p>
            <form onSubmit={handleLogin} className="space-y-4">
              <input
                type="password"
                data-testid="admin-password-input"
                placeholder={t("password")}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="w-full bg-[#0f0f0f] border border-[#252525] rounded-lg px-4 py-3 text-white focus:border-[#e8559a] focus:ring-2 focus:ring-[#e8559a]/30 focus:outline-none"
              />
              {error && (
                <p data-testid="admin-error" className="text-[#e05555] text-sm">
                  {error}
                </p>
              )}
              <button
                type="submit"
                data-testid="admin-login-btn"
                className="w-full bg-[#e8559a] hover:bg-[#c9407f] text-white font-bebas text-xl tracking-[0.2em] rounded-xl py-3 transition-colors"
              >
                {t("enter")} →
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  const positions = ["forward", "midfielder", "defender", "goalkeeper"];

  return (
    <div className="min-h-screen bg-[#080808] text-white">
      <TopBar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-bebas text-4xl sm:text-5xl tracking-[0.1em]">
              {t("admin_title")}
            </h1>
            <p className="text-sm text-neutral-400 mt-1">
              {t("total_players")}: <span className="text-[#e8559a] font-bebas text-xl tracking-wider" data-testid="total-count">{filtered.length}</span>
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => navigate("/admin/calendar")}
              data-testid="open-calendar-btn"
              className="px-4 py-2 rounded-lg bg-[#161616] border border-[#e8559a]/30 hover:border-[#e8559a] text-[#e8559a] text-sm font-medium transition-colors"
            >
              📅 {t("calendar")}
            </button>
            <button
              onClick={() => navigate("/admin/payments")}
              data-testid="open-payments-btn"
              className="px-4 py-2 rounded-lg bg-[#161616] border border-[#e8559a]/30 hover:border-[#e8559a] text-[#e8559a] text-sm font-medium transition-colors"
            >
              💳 {t("payments")}
            </button>
            <button
              onClick={() => navigate("/admin/demo")}
              data-testid="open-demo-btn"
              className="px-4 py-2 rounded-lg bg-[#161616] border border-[#e8559a]/30 hover:border-[#e8559a] text-[#e8559a] text-sm font-medium transition-colors"
            >
              ★ Demo Profile
            </button>
            <button
              onClick={() => navigate("/admin/elite")}
              data-testid="open-elite-btn"
              className="px-4 py-2 rounded-lg bg-[#161616] border text-sm font-medium transition-colors"
              style={{ borderColor: "#39ff1440", color: "#39ff14" }}
            >
              ⚡ Elite Apps
            </button>
            <button
              onClick={exportCSV}
              data-testid="export-csv-btn"
              className="px-4 py-2 rounded-lg bg-[#e8559a] hover:bg-[#c9407f] text-white text-sm font-medium transition-colors"
            >
              ⬇ {t("export_csv")}
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="px-4 py-2 rounded-lg border border-[#252525] hover:border-[#e8559a]/50 text-neutral-300 text-sm transition-colors"
            >
              {t("logout")}
            </button>
          </div>
        </div>

        <SharePanel />
        <PrivateRequestsPanel />

        <div className="rounded-xl bg-[#161616] border border-[#252525] p-4 mb-6 flex flex-col sm:flex-row gap-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("search")}
            data-testid="admin-search-input"
            className="flex-1 bg-[#0f0f0f] border border-[#252525] rounded-lg px-4 py-2.5 text-white placeholder:text-neutral-600 focus:border-[#e8559a] focus:outline-none"
          />
          <select
            value={filterPos}
            onChange={(e) => setFilterPos(e.target.value)}
            data-testid="admin-filter-position"
            className="bg-[#0f0f0f] border border-[#252525] rounded-lg px-4 py-2.5 text-white focus:border-[#e8559a] focus:outline-none"
          >
            <option value="">{t("all_positions")}</option>
            {positions.map((p) => (
              <option key={p} value={p}>
                {t(`pos_${p}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="rounded-xl bg-[#161616] border border-[#252525] overflow-hidden" data-testid="players-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#0f0f0f] text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                <tr>
                  {[
                    "table_number","table_name","table_age","table_position","table_foot","table_club","table_parent","table_email","table_phone","table_date","table_actions"
                  ].map((k) => (
                    <th key={k} className="text-left px-4 py-3 whitespace-nowrap">{t(k)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={11} className="px-4 py-8 text-center text-neutral-500">...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={11} className="px-4 py-12 text-center text-neutral-500">{t("no_players")}</td></tr>
                ) : (
                  filtered.map((p) => (
                    <tr
                      key={p.id}
                      onClick={() => navigate(`/admin/player/${p.id}`)}
                      className="border-t border-[#252525] hover:bg-[#1c1c1c] transition-colors cursor-pointer"
                      data-testid={`row-${p.player_number}`}
                    >
                      <td className="px-4 py-3 font-bebas text-lg text-[#e8559a] tracking-widest">{p.player_number}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{p.first_name} {p.last_name}</td>
                      <td className="px-4 py-3">{p.age}</td>
                      <td className="px-4 py-3 capitalize">{lang === 'es' ? t(`pos_${p.position}`) : p.position}</td>
                      <td className="px-4 py-3 capitalize">{p.dominant_foot}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{p.current_club || "—"}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{p.parent_full_name}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{p.email}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{p.phone}</td>
                      <td className="px-4 py-3 text-neutral-400 whitespace-nowrap text-xs">
                        {new Date(p.timestamp).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => navigate(`/admin/player/${p.id}`)}
                            data-testid={`view-${p.player_number}`}
                            className="px-2.5 py-1.5 rounded-md bg-[#e8559a]/10 border border-[#e8559a]/30 text-[#e8559a] text-xs font-medium hover:bg-[#e8559a]/20 transition-colors"
                          >
                            👤 {t("view_profile")}
                          </button>
                          <button
                            onClick={() => setEditing(p)}
                            data-testid={`edit-${p.player_number}`}
                            className="px-2.5 py-1.5 rounded-md bg-[#1f1f1f] border border-[#333] hover:border-[#e8559a]/50 text-neutral-200 text-xs font-medium transition-colors"
                          >
                            ✎
                          </button>
                          <button
                            onClick={() => exportPDF(p)}
                            data-testid={`pdf-${p.player_number}`}
                            className="px-2.5 py-1.5 rounded-md bg-[#1f1f1f] border border-[#333] hover:border-[#e8559a]/50 text-neutral-200 text-xs font-medium transition-colors"
                          >
                            📄
                          </button>
                          <button
                            onClick={() => { setDeleteError(""); setDeleting(p); }}
                            data-testid={`delete-${p.player_number}`}
                            className="px-2.5 py-1.5 rounded-md bg-[#e05555]/10 border border-[#e05555]/30 text-[#e05555] text-xs font-medium hover:bg-[#e05555]/20 transition-colors"
                          >
                            🗑
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
      {editing && (
        <EditPlayerModal
          player={editing}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}
      {deleting && (
        <div
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="delete-modal"
          onClick={() => !deleteBusy && setDeleting(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-[#161616] border border-[#e05555]/40 rounded-2xl p-6 sm:p-7"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-[#e05555]/15 border border-[#e05555]/40 flex items-center justify-center text-2xl flex-shrink-0">
                🗑
              </div>
              <div className="min-w-0">
                <h3 className="font-bebas text-2xl tracking-widest text-white leading-tight">
                  {t("delete")}
                </h3>
                <p className="text-sm text-neutral-400 mt-1">
                  {t("delete_confirm")}
                </p>
              </div>
            </div>
            <div className="rounded-lg bg-[#0f0f0f] border border-[#252525] px-4 py-3 mb-4 flex items-center gap-3">
              <span className="font-bebas text-2xl text-[#e8559a] tracking-widest">
                #{deleting.player_number}
              </span>
              <span className="text-white font-medium truncate">
                {deleting.first_name} {deleting.last_name}
              </span>
            </div>
            {deleteError && (
              <div
                data-testid="delete-error"
                className="mb-3 p-3 rounded-lg bg-[#e05555]/10 border border-[#e05555]/40 text-[#e05555] text-sm"
              >
                {deleteError}
              </div>
            )}
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <button
                type="button"
                disabled={deleteBusy}
                onClick={() => setDeleting(null)}
                data-testid="delete-cancel-btn"
                className="px-5 py-2.5 rounded-lg border border-[#252525] hover:border-[#e8559a]/50 text-neutral-300 transition-colors disabled:opacity-60"
              >
                {t("cancel")}
              </button>
              <button
                type="button"
                disabled={deleteBusy}
                onClick={() => handleDelete(deleting)}
                data-testid="delete-confirm-btn"
                className="px-6 py-2.5 rounded-lg bg-[#e05555] hover:bg-[#c34444] disabled:opacity-60 text-white font-bebas text-lg tracking-widest transition-colors"
              >
                {deleteBusy ? "..." : `🗑 ${t("delete")}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
